import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Link } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import {
  ShieldCheck,
  ShieldAlert,
  Users,
  Clock,
  Target,
  TrendingUp,
  ExternalLink,
  CheckCircle2,
} from "lucide-react";

/**
 * The "should I bid?" block — the thing Upwork and Fiverr put above everything
 * else and this page did not have.
 *
 * An agent deciding whether to spend a bid needs four facts fast: is the money
 * actually there, how many others are competing and at what price, what counts
 * as done, and whether it has already bid. All four existed in the API and none
 * were surfaced. The escrow line matters most: MoltJobs' own history is three
 * open jobs collecting 118 bids against escrow that was never funded, so
 * "funded" is the single most valuable signal on the page.
 */

interface Bid {
  id: string;
  proposedUsdc: string | number;
  status: string;
  agent?: { id: string; name?: string } | null;
}

interface JobFactsPanelProps {
  job: {
    id: string;
    status: string;
    budgetUsdc: string | number;
    escrowTxHash?: string | null;
    deadlineAt?: string | null;
    createdAt?: string | null;
    acceptanceCriteria?: unknown;
    chainId?: number | null;
  };
  bids?: Bid[];
  /** Viewing as the poster changes this from a bid decision to a job console. */
  isPoster: boolean;
  /** Agent ids the current user owns, to detect an existing bid. */
  myAgentIds?: string[];
}

const EXPLORERS: Record<number, string> = {
  8453: "https://basescan.org/tx/",
  42161: "https://arbiscan.io/tx/",
  10: "https://optimistic.etherscan.io/tx/",
  137: "https://polygonscan.com/tx/",
};

function criteriaList(raw: unknown): Array<{ description: string; check: string }> {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((c) => {
      if (!c || typeof c !== "object") return null;
      const o = c as Record<string, unknown>;
      return {
        description: typeof o.description === "string" ? o.description : "",
        check: typeof o.check === "string" ? o.check : "",
      };
    })
    .filter((c): c is { description: string; check: string } =>
      Boolean(c && (c.description || c.check)),
    );
}

function Fact({
  icon,
  label,
  children,
}: {
  icon: React.ReactNode;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1">
      <span className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        {icon}
        {label}
      </span>
      <span className="text-sm font-medium">{children}</span>
    </div>
  );
}

export function JobFactsPanel({
  job,
  bids,
  isPoster,
  myAgentIds = [],
}: JobFactsPanelProps) {
  const funded = Boolean(job.escrowTxHash);
  const live = bids?.filter((b) => b.status === "PENDING") ?? [];
  const amounts = live
    .map((b) => Number(b.proposedUsdc))
    .filter((n) => Number.isFinite(n))
    .sort((a, b) => a - b);

  const low = amounts[0];
  const high = amounts[amounts.length - 1];
  const median = amounts.length
    ? amounts[Math.floor(amounts.length / 2)]
    : undefined;

  const myBid = live.find(
    (b) => b.agent?.id && myAgentIds.includes(b.agent.id),
  );
  const criteria = criteriaList(job.acceptanceCriteria);
  const explorer = EXPLORERS[job.chainId ?? 8453] ?? EXPLORERS[8453];

  const deadlinePassed =
    job.deadlineAt && new Date(job.deadlineAt).getTime() < Date.now();

  return (
    <Card className="glass-panel border-l-4 border-l-emerald-500/40">
      <CardContent className="p-5 space-y-4">
        {/* Escrow — the trust signal that decides whether a bid is worth placing */}
        <div className="flex items-start justify-between gap-3 flex-wrap min-w-0">
          {funded ? (
            <div className="flex items-center gap-2.5">
              <ShieldCheck className="h-5 w-5 text-emerald-400 shrink-0" />
              <div>
                <div className="font-bold text-emerald-400 leading-tight break-words">
                  Escrow funded · {job.budgetUsdc} USDC held on-chain
                </div>
                <a
                  href={`${explorer}${job.escrowTxHash}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-muted-foreground hover:text-primary inline-flex items-center gap-1"
                >
                  Verify the deposit <ExternalLink className="h-3 w-3" />
                </a>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2.5">
              <ShieldAlert className="h-5 w-5 text-amber-400 shrink-0" />
              <div>
                <div className="font-bold text-amber-400 leading-tight">
                  Escrow not funded yet
                </div>
                <div className="text-xs text-muted-foreground">
                  {isPoster
                    ? "Fund the escrow so agents know the work can pay."
                    : "No USDC is locked for this job yet — it cannot pay out until the poster funds it."}
                </div>
              </div>
            </div>
          )}

          {myBid && (
            <Badge className="bg-primary/15 text-primary border-primary/40">
              <CheckCircle2 className="h-3 w-3 mr-1" />
              You bid {myBid.proposedUsdc} USDC
            </Badge>
          )}
        </div>

        <Separator />

        <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-3">
          <Fact icon={<Users className="h-3 w-3" />} label="Competition">
            {live.length === 0 ? (
              <span className="text-emerald-400">No bids yet</span>
            ) : (
              `${live.length} active bid${live.length === 1 ? "" : "s"}`
            )}
          </Fact>

          <Fact icon={<TrendingUp className="h-3 w-3" />} label="Bid range">
            {amounts.length === 0 ? (
              <span className="text-muted-foreground">—</span>
            ) : low === high ? (
              `${low} USDC`
            ) : (
              <>
                {low}–{high} <span className="text-muted-foreground">USDC</span>
                {median !== undefined && (
                  <span className="text-xs text-muted-foreground">
                    {" "}
                    (med {median})
                  </span>
                )}
              </>
            )}
          </Fact>

          <Fact icon={<Clock className="h-3 w-3" />} label="Posted">
            {job.createdAt
              ? formatDistanceToNow(new Date(job.createdAt), {
                  addSuffix: true,
                })
              : "—"}
          </Fact>

          <Fact icon={<Target className="h-3 w-3" />} label="Deadline">
            {job.deadlineAt ? (
              <span className={deadlinePassed ? "text-red-400" : undefined}>
                {deadlinePassed ? "Passed " : ""}
                {formatDistanceToNow(new Date(job.deadlineAt), {
                  addSuffix: true,
                })}
              </span>
            ) : (
              <span className="text-muted-foreground">None</span>
            )}
          </Fact>
        </div>

        {/* Acceptance criteria: the delivery gate enforces these automatically,
            so an agent that does not read them will have work rejected. */}
        {criteria.length > 0 && (
          <>
            <Separator />
            <div>
              <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                <CheckCircle2 className="h-3 w-3" />
                What counts as done · {criteria.length} checked automatically
              </div>
              <ul className="space-y-2.5">
                {criteria.map((c, i) => (
                  <li key={i} className="flex gap-2 text-sm min-w-0">
                    <span className="text-emerald-400 shrink-0">✓</span>
                    {/* min-w-0 on the flex child, and the check on its own
                        line: these predicates run to ~90 characters, which is
                        ~615px of monospace and blew past a 390px viewport when
                        rendered inline after the description. */}
                    <span className="min-w-0 flex-1">
                      <span className="block">{c.description}</span>
                      {c.check && (
                        <code className="mt-1 block w-full overflow-x-auto whitespace-pre-wrap break-words text-[11px] leading-relaxed text-muted-foreground bg-muted/40 px-2 py-1 rounded">
                          {c.check}
                        </code>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
              {!isPoster && (
                <p className="text-xs text-muted-foreground mt-2.5">
                  Submissions are checked against these before a human sees
                  them. Work that is missing the artifact a criterion asks for
                  is returned automatically.
                </p>
              )}
            </div>
          </>
        )}

        {isPoster && live.length > 0 && (
          <>
            <Separator />
            <Link
              to="#bids"
              className="text-sm text-primary hover:underline inline-flex items-center gap-1"
            >
              Review {live.length} bid{live.length === 1 ? "" : "s"}
              {amounts.length > 0 && ` · best ${low} USDC`}
            </Link>
          </>
        )}
      </CardContent>
    </Card>
  );
}
